import { lazy, type ComponentType } from 'react';
import { reloadForNewBuild, STALE_CHUNK_ERROR } from './staleBuild';

/** Every lazily loaded file (since 2026-10-15 just the signed-in app, AuthenticatedApp), for prefetchPages(). */
const pageLoaders: Array<() => Promise<unknown>> = [];

/**
 * `React.lazy` for a module's NAMED export. When the file can't be loaded because a deploy replaced
 * it, reload onto the new build (staleBuild.ts, a few tries); any other error propagates to the error
 * boundary as usual.
 */
export function lazyPage<M extends Record<string, unknown>, K extends keyof M & string>(
  load: () => Promise<M>, name: K,
) {
  pageLoaders.push(load);
  return lazy(async () => {
    try {
      const module = await load();
      // Undefined when a file the module needs failed to preload and installStaleBuildReload took it (a reload
      // onto the new build is under way): Vite then resolves the import with nothing instead of throwing. Keep
      // showing the loading state until the reload happens (a production build only; found 2026-10-15).
      if (module === undefined) return new Promise<never>(() => { /* the page is reloading */ });
      return { default: module[name] as unknown as ComponentType };
    } catch (error) {
      if (STALE_CHUNK_ERROR.test(String((error as Error)?.message ?? error)) && reloadForNewBuild()) {
        return new Promise<never>(() => { /* the page is reloading */ });
      }
      throw error;
    }
  });
}

type IdleWindow = Window & {
  requestIdleCallback?: (callback: () => void, options?: { timeout: number }) => number;
};
type NetworkInformation = { saveData?: boolean; effectiveType?: string };
let prefetchStarted = false;

/**
 * Fetch the lazily loaded files in the background once the browser is idle: the sign-in page fetches
 * the signed-in app this way, so it's ready when the visitor has signed in. One file at a time, each in
 * an idle moment, so it never competes with the page being used. Skipped when the visitor asked to save
 * data or is on a 2G connection. A file that fails here is ignored: it loads (and recovers) normally
 * when it's needed. Once per tab.
 */
export function prefetchPages(): void {
  if (prefetchStarted) return;
  prefetchStarted = true;
  const connection = (navigator as Navigator & { connection?: NetworkInformation }).connection;
  if (connection?.saveData || /(^|-)2g$/.test(connection?.effectiveType ?? '')) return;

  const idle = (callback: () => void) => {
    const w = window as IdleWindow;
    if (w.requestIdleCallback) w.requestIdleCallback(callback, { timeout: 5_000 });
    else window.setTimeout(callback, 200);
  };
  const queue = [...pageLoaders];
  const next = () => {
    const load = queue.shift();
    if (!load) return;
    idle(() => { load().catch(() => { /* loads normally when opened */ }).finally(next); });
  };
  next();
}
